import { useParams, useLocation, useNavigate } from 'react-router-dom'
import type { Dictionary } from 'lodash'
import { identity, pickBy } from 'lodash'

export function useRouteQuery() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()

  // Parse search params từ URL
  const searchQuery = new URLSearchParams(location.search)

  function serializeQuery<T extends object = Dictionary<unknown>>(query: T) {
    return pickBy(query, identity)
  }

  function updateQuery(query: Record<string, undefined | string | number>, replace = true) {
    // Lấy current search params
    const currentParams = Object.fromEntries(searchQuery.entries())

    // Merge với new query params
    const newQuery = pickBy(
      {
        ...currentParams,
        ...query
      },
      identity
    )

    const searchString = new URLSearchParams(newQuery as Record<string, string>).toString()
    const newUrl = searchString ? `${location.pathname}?${searchString}` : location.pathname

    if (replace) {
      navigate(newUrl, { replace: true })
    } else {
      navigate(newUrl)
    }
  }

  // Helper function để lấy search param value
  function getSearchParam(key: string): string | null {
    return searchQuery.get(key)
  }

  // Helper function để lấy tất cả search params
  function getAllSearchParams(): Record<string, string> {
    return Object.fromEntries(searchQuery.entries())
  }

  // Helper function để set single search param
  function setSearchParam(key: string, value: string | number | undefined, replace = true) {
    updateQuery({ [key]: value }, replace)
  }

  // Helper function để remove search param
  function removeSearchParam(key: string, replace = true) {
    const currentParams = Object.fromEntries(searchQuery.entries())
    delete currentParams[key]

    const searchString = new URLSearchParams(currentParams).toString()
    const newUrl = searchString ? `${location.pathname}?${searchString}` : location.pathname

    if (replace) {
      navigate(newUrl, { replace: true })
    } else {
      navigate(newUrl)
    }
  }

  // Helper function để clear all search params
  function clearSearchParams(replace = true) {
    if (replace) {
      navigate(location.pathname, { replace: true })
    } else {
      navigate(location.pathname)
    }
  }

  return {
    navigate,
    params,
    path: location.pathname,
    search: location.search,
    searchQuery,
    updateQuery,
    serializeQuery,
    getSearchParam,
    getAllSearchParams,
    setSearchParam,
    removeSearchParam,
    clearSearchParams
  }
}
